import { useEffect, useState } from 'react'
import { BsCalendar3, BsChevronDown } from 'react-icons/bs'
import { format } from 'date-fns'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { Calendar } from 'components/UI/calendar'
import { EventHistoryList } from 'components/NewCompanyPage/EventHistory/EventHistoryList'
import { EventHistoryFilterModal } from 'components/NewCompanyPage/EventHistory/EventHistoryFilterModal'
import { EventHistoryFilterChips } from 'components/NewCompanyPage/EventHistory/EventHistoryFilterChips'
import { useEventHistoryQuery } from 'components/NewCompanyPage/EventHistory/useEventHistoryQuery'
import { buildEmailFromEvent } from 'components/NewCompanyPage/EventHistory/utils'
import { EmailsModal } from 'components/NewCompanyPage/Modals/EmailsModal/EmailsModal'
import { useEventHistoryFilterStore } from 'components/NewCompanyPage/EventHistory/EventHistoryFilterStore'
import { StageColorProvider } from 'components/SalesStage/StageColorContext'
import type { EventHistoryTab } from 'components/NewCompanyPage/EventHistory/EventHistoryAccordion'
import type { EmailMessage } from 'models/emailIntegration'

interface DealEventHistoryProps {
  dealId: number
}

/**
 * Deal-scoped activity feed. Reuses the same sub-components as the company
 * page EventHistoryAccordion but without the accordion wrapper, company-specific
 * tabs (Deals, Samples, Account Attributes), or sample preview modal.
 */
export function DealEventHistory({ dealId }: DealEventHistoryProps) {
  const [selectedTab] = useState<EventHistoryTab>('all')
  const [dateRange, setDateRange] = useState<
    { from: Date; to?: Date } | undefined
  >(undefined)

  // Reset shared filter store on mount to prevent cross-page filter leakage
  useEffect(() => {
    useEventHistoryFilterStore.getState().clearAllFilters()
  }, [dealId])

  const {
    groupedEvents,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useEventHistoryQuery({
    dealId,
    selectedTab,
    dateRange,
  })

  // Email view modal state
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null)

  return (
    <StageColorProvider value="pipeline">
      <div className="space-y-4">
        {/* Filters Row */}
        <div className="w-full flex gap-2">
          {/* Date Range Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <button className="flex-1 flex items-center gap-2 px-3.5 py-2 bg-surface border border-line rounded-md text-sm text-fg hover:border-brand transition-colors">
                <BsCalendar3 className="w-5 h-5 flex-shrink-0" />
                <span className="flex-1 text-left">
                  {dateRange?.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, 'd MMM yyyy')} -{' '}
                        {format(dateRange.to, 'd MMM yyyy')}
                      </>
                    ) : (
                      format(dateRange.from, 'd MMM yyyy')
                    )
                  ) : (
                    'All time'
                  )}
                </span>
                <BsChevronDown className="w-5 h-5 flex-shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={(newRange) => {
                  if (!newRange?.from) return
                  setDateRange(newRange as { from: Date; to?: Date })
                }}
                numberOfMonths={2}
              />
              {dateRange && (
                <div className="p-3 border-t">
                  <button
                    onClick={() => setDateRange(undefined)}
                    className="w-full px-3 py-1.5 text-sm text-fg-secondary hover:text-brand transition-colors"
                  >
                    Clear dates
                  </button>
                </div>
              )}
            </PopoverContent>
          </Popover>

          {/* More Filters Button */}
          <EventHistoryFilterModal
            selectedTab={selectedTab}
            availableEventTypes={['deal', 'notes', 'reminder', 'email']}
          />
        </div>

        {/* Filter Chips */}
        <EventHistoryFilterChips selectedTab={selectedTab} />

        {/* Event List */}
        <EventHistoryList
          isLoading={isLoading}
          error={error}
          groupedEvents={groupedEvents}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onPreviewRequest={() => {}}
          onEmailClick={(event) => setSelectedEmail(buildEmailFromEvent(event))}
        />

        {/* Email View Modal */}
        {selectedEmail && (
          <EmailsModal
            show={!!selectedEmail}
            onHide={() => setSelectedEmail(null)}
            emailOnlyMode
            initialEmail={selectedEmail}
          />
        )}
      </div>
    </StageColorProvider>
  )
}
